// TW Conversion

import { FunctionComponent } from 'react';
import CTAButton from '~/components/shared/CTAButton';
import TooltipV2 from '~/components/shared/TooltipV2';
import { handleSuperscripts, isEmpty } from '~/helper/editableHelpers';
import { useMonarchSlotContext } from '@redventures/cohesion-utils-react';
import { useVariantIds } from '~/lib/hooks/useVariantIds';
import Image from 'next-export-optimize-images/image';

// Classes
import {
  getBgColor,
  getTextColor,
  wrapperClasses,
  headlineClasses,
  subheadlineClasses,
  tooltipClasses,
  ctaWrapperClasses,
  primaryCTAClasses,
} from './simplifiedSolidHeroClasses';

// Types and Default Props
import { DEFAULT_PROPS } from './data';

const SimplifiedSolidHero: FunctionComponent = () => {
  const { data } = useMonarchSlotContext<typeof DEFAULT_PROPS>();

  const { isDarkMode } = useVariantIds();

  const {
    variant,
    headline,
    subheadline,
    disclaimer,
    disclaimerLink,
    CTAButtonText,
    CTAButtonVariant,
    CTAButtonLink,
    hasSecondaryCTAButton,
    secondaryCTAButtonText,
    secondaryCTAButtonVariant,
    secondaryCTAButtonLink,
  } = data ?? DEFAULT_PROPS;

  const headlineWithSuperscript = handleSuperscripts(headline);
  const subheadlineWithSuperscript = handleSuperscripts(subheadline);
  const variantClasses = isDarkMode ? 'dark' : variant;

  // Classes
  const bgColorClasses = getBgColor(variantClasses);
  const textColorClasses = getTextColor(variantClasses);

  const imgPath = `${process.env.domainPath}/images/sections/hero/`;
  const presentImage = 'holiday-present.png';
  const ornamentImage = 'holiday-ornament.png';
  const candyCaneImage = 'holiday-candy-cane.png';

  const isHoliday = variant === 'holiday';

  return (
    <div
      className={`${wrapperClasses} ${bgColorClasses} relative overflow-hidden`}
    >
      {/* Holiday Decorations - Only show for holiday variant */}
      {isHoliday && (
        <>
          {/* Present stack - left side, vertically centered */}
          <Image
            src={`${imgPath}${presentImage}`}
            alt='Holiday presents'
            width={200}
            height={200}
            className='absolute left-0 lg:left-10 xl:left-[15rem] top-[9rem] md:top-[5rem] lg:top-8 w-auto h-[63px] md:h-[111px] lg:h-[180px] lg:h-[220px]'
          />
          {/* Ornament - top right */}
          <Image
            src={`${imgPath}${ornamentImage}`}
            alt='Holiday ornament'
            width={120}
            height={120}
            className='absolute -top-2 md:top-0 right-0 lg:right-[5rem] xl:right-[15rem] w-auto h-[72px] lg:h-[100px] xl:h-[120px]'
          />
          {/* Candy cane - top right */}
          <Image
            src={`${imgPath}${candyCaneImage}`}
            alt='Candy cane'
            width={180}
            height={180}
            className='absolute top-[9rem] lg:top-[4rem] xl:top-6 right-0 w-[60px] h-[50px] md:top-[9rem] md:w-[85px] md:h-[71px] lg:w-[200px] lg:h-[170px] xl:w-[243px] xl:h-[203px]'
          />
        </>
      )}

      {/* Content */}
      <h1 className={`${headlineClasses} ${textColorClasses} relative z-10`}>
        {headlineWithSuperscript}
      </h1>
      <p className={`${subheadlineClasses} ${textColorClasses} relative z-10`}>
        <span className='items-center'>
          {subheadlineWithSuperscript}
          {!isEmpty(disclaimer) && (
            <TooltipV2
              text={disclaimer}
              link={disclaimerLink}
              className={tooltipClasses}
              elementPosition='SIMPLIFIED_SOLID_HERO'
            />
          )}
        </span>
      </p>
      <div className={`${ctaWrapperClasses} relative z-10`}>
        <CTAButton
          elementPosition='HERO'
          elementName='SIMPLIFIED_SOLID_CTA'
          variant={CTAButtonVariant}
          elementType='SIGN_UP_CTA_PRIMARY'
          href={CTAButtonLink}
          className={primaryCTAClasses}
        >
          {CTAButtonText}
        </CTAButton>
        {hasSecondaryCTAButton && (
          <CTAButton
            elementPosition='HERO'
            elementName='SIMPLIFIED_SOLID_CTA'
            variant={secondaryCTAButtonVariant}
            elementType='SIGN_UP_CTA_SECONDARY'
            href={secondaryCTAButtonLink}
            className={
              secondaryCTAButtonVariant === 'text-link' ? textColorClasses : ''
            }
          >
            {secondaryCTAButtonText}
          </CTAButton>
        )}
      </div>
    </div>
  );
};

export default SimplifiedSolidHero;
